'use client';

import * as React from 'react';
import Autoplay from 'embla-carousel-autoplay';
import { GrindingUnitsProps } from '@/types/plants';
import Container from '@/components/ui/Container/Container';
import CmsImage from '@/components/ui/CmsImage';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { plantImageAt } from '../plantPageAssets';
import { getImageUrlForVariant } from '@/lib/homepage-mappers';

const CARD_W = 'w-[min(78vw,260px)] sm:w-[280px] md:w-[300px]';
const CARD_H = 'h-auto md:h-[380px]';

function PinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <path
        fillRule="evenodd"
        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
        clipRule="evenodd"
      />
    </svg>
  );
}

export default function GrindingUnits({ data }: GrindingUnitsProps) {
  const [api, setApi] = React.useState<CarouselApi>();
  const [selectedSnap, setSelectedSnap] = React.useState(0);
  const [snapCount, setSnapCount] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const autoplayPlugin = React.useRef(
    Autoplay({
      delay: 5000,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
    }),
  );

  const cards = (data.units ?? []).map((u, idx) => ({
    id: idx + 1,
    state: u.state,
    name: u.location,
    since: u.operationalYear || '',
    capacityLabel: u.capacity,
    imageAlt: u.image?.alternativeText || u.location || 'Grinding unit',
    imageSrc: getImageUrlForVariant(u.image, 'large') || plantImageAt(idx),
  }));

  React.useEffect(() => {
    if (!api) return;

    const updateSnapState = () => {
      setSelectedSnap(api.selectedScrollSnap());
      setSnapCount(api.scrollSnapList().length);
    };

    const onPointerDown = () => setIsDragging(true);
    const onPointerUp = () => setIsDragging(false);

    updateSnapState();
    api.on('select', updateSnapState);
    api.on('reInit', updateSnapState);
    api.on('pointerDown', onPointerDown);
    api.on('pointerUp', onPointerUp);
    window.addEventListener('pointerup', onPointerUp);

    return () => {
      api.off('select', updateSnapState);
      api.off('reInit', updateSnapState);
      api.off('pointerDown', onPointerDown);
      api.off('pointerUp', onPointerUp);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [api]);

  if (cards.length === 0) return null;

  return (
    <section className="bg-[#f7f4ef] text-gray-900">
      <Container>
        <header className="mb-8 text-center sm:mb-10">
          <h2 className="text-[1.75rem] font-semibold tracking-tight text-gray-900 sm:text-[2rem] md:text-5xl lg:text-[3.25rem]">
            {data.heading}
          </h2>
          {data.subHeadingHtml ? (
            <div
              className="mx-auto mt-4 max-w-5xl text-sm leading-relaxed text-gray-600 sm:mt-5 sm:text-sm md:text-base [&_p]:m-0 [&_strong]:font-semibold [&_strong]:text-gray-900"
              dangerouslySetInnerHTML={{ __html: data.subHeadingHtml }}
            />
          ) : null}
        </header>
      </Container>

      <div className="mt-2 flex justify-center">
        <Carousel
          setApi={setApi}
          opts={{
            align: 'start',
            loop: false,
            dragFree: true,
            containScroll: 'trimSnaps',
          }}
          plugins={[autoplayPlugin.current]}
          className={`w-full max-w-[1600px] px-4 pb-4 pt-1 select-none sm:px-6 md:px-8 ${isDragging ? 'cursor-grabbing' : 'cursor-default'}`}
        >
          <CarouselContent className="-ml-0 gap-4 sm:gap-5 md:gap-6">
            {cards.map((card) => (
              <CarouselItem key={card.id} className="basis-auto pl-0">
                <article className={`group/card ${CARD_W} ${CARD_H}`}>
                  <div className="relative flex min-h-[360px] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_2px_24px_rgba(0,0,0,0.06)] ring-1 ring-black/[0.04] transition-shadow duration-300 ease-out hover:shadow-[0_16px_48px_-8px_rgba(0,0,0,0.12)] md:h-full md:min-h-0">
                    <div className="relative order-2 z-0 flex flex-1 flex-col justify-end bg-white px-4 pb-4 pt-3 sm:px-5 sm:pb-5 sm:pt-4 md:absolute md:inset-0">
                      <div className="opacity-100 transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.33,1,0.68,1)] [transform:translate3d(0,0,0)] md:opacity-0 md:[transform:translate3d(0,10px,0)] md:group-hover/card:opacity-100 md:group-hover/card:[transform:translate3d(0,0,0)] md:group-hover/card:delay-75 motion-reduce:opacity-100 motion-reduce:[transform:translate3d(0,0,0)] motion-reduce:transition-none motion-reduce:delay-0">
                        <h3 className="text-base font-semibold tracking-tight text-gray-900 sm:text-lg">
                          {card.name}
                        </h3>
                        <div className="mt-3 grid grid-cols-2 gap-4 sm:mt-4 sm:gap-6">
                          <div>
                            <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500 sm:text-xs">
                              Commissioned
                            </div>
                            <div className="mt-1 text-base font-semibold text-gray-900 sm:text-lg">
                              {card.since}
                            </div>
                          </div>
                          <div>
                            {/* <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500 sm:text-xs">
                              Capacity
                            </div> */}
                            <div className="mt-1 text-base font-semibold text-gray-900 sm:text-lg">
                              {card.capacityLabel}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="relative order-1 z-10 aspect-[4/3] w-full shrink-0 overflow-hidden rounded-t-2xl bg-neutral-200 shadow-[0_4px_20px_rgba(0,0,0,0.08)] [backface-visibility:hidden] [transform:translateZ(0)] transition-[clip-path] duration-500 ease-[cubic-bezier(0.33,1,0.68,1)] md:absolute md:left-0 md:right-0 md:top-0 md:h-full md:aspect-auto md:rounded-2xl md:[clip-path:inset(0_0_0_0)] md:group-hover/card:[clip-path:inset(0_0_42%_0)] motion-reduce:transition-none">
                      <div className="relative h-full w-full">
                        <CmsImage
                          src={card.imageSrc}
                          alt={card.imageAlt}
                          fill
                          className="object-contain [backface-visibility:hidden] [transform:translate3d(0,0,0)] md:object-cover"
                          sizes="(max-width: 640px) 78vw, 300px"
                          draggable={false}
                          fallbackSrc={MEDIA_PLACEHOLDER}
                        />
                        <div
                          className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/20 to-transparent opacity-50"
                          aria-hidden
                        />
                        <div className="pointer-events-none absolute left-3 top-3 md:left-4 md:top-4">
                          <div className="inline-flex items-center gap-1.5 rounded-full bg-white/95 px-2.5 py-1 text-[12px] font-medium text-gray-900 shadow-sm sm:px-3 sm:py-1.5 sm:text-[13px] md:text-sm">
                            <PinIcon className="h-3.5 w-3.5 shrink-0 text-red-500" />
                            {card.state}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              </CarouselItem>
            ))}
          </CarouselContent>

          {snapCount > 1 ? (
            <div className="mt-7 flex items-center justify-center gap-4 sm:mt-8">
              <CarouselPrevious className="static h-10 w-10 translate-y-0 rounded-full border border-gray-300 bg-white text-gray-500 shadow-sm transition-colors hover:border-[#ed1c22] disabled:pointer-events-none disabled:opacity-35" />
              <div className="flex items-center gap-2" aria-label="Grinding unit slides">
                {Array.from({ length: snapCount }).map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => api?.scrollTo(index)}
                    className={`rounded-full transition-all duration-300 ${
                      selectedSnap === index
                        ? 'h-2.5 w-7 bg-[#ed1c22]'
                        : 'h-2.5 w-2.5 bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`Go to grinding unit slide ${index + 1}`}
                    aria-current={selectedSnap === index ? 'true' : undefined}
                  />
                ))}
              </div>
              <CarouselNext className="static h-10 w-10 translate-y-0 rounded-full border border-gray-300 bg-white text-gray-500 shadow-sm transition-colors hover:border-[#ed1c22] disabled:pointer-events-none disabled:opacity-35" />
            </div>
          ) : null}
        </Carousel>
      </div>
    </section>
  );
}
